import { For, If, location, onCleanup, onMount, reactive } from 'cdui-js';
import i18n from '../../../i18n';
import { goBackByBrowser, routeReplace } from '../../../lib/browser';
import { fetchStockAccountsMeta } from '../../../api/funds';
import { saveStockTransferInApply } from '../../../api/stockTransferIn';
import { bootDepositApp } from '../deposit/h5-deposit/appBridge';
import { awaitUserInfoReady } from '../../../stores/user';
import { showActionSheet } from '../components/ActionSheet';
import { ActionSheetCheckIcon } from '../components/ActionSheet/CheckIcon';
import { DiFiPreview } from '../../../components/DiFi/DiFiPreview';
import { AuthSensitiveImage } from '../components/AuthSensitiveImage';
import { showToast, showToastError } from '../components/Toast';
import { StiInfoRow, StiInfoSectionHead, StiStockInfoCards } from './InfoBlocks';
import { StockTransferNav } from './Nav';
import {
  applyInboundAccountToDraft,
  buildStockTransferApplySaveBody,
  displayInAccountTypeForDraft,
  displayTransferField,
  parseStockTransferApplyId,
  validateStockTransferBeforeSubmit,
  displayInboundCcassIdForTransfer,
  getStockTransferApplyId,
  getStockTransferDraft,
  listInboundSecuritiesAccounts,
  pickInboundAccountOption,
  readMarketQuery,
  resolveInAccountNum,
  setStockTransferApplyId,
  setStockTransferDraft,
  statementProofFileName,
  statementProofKindFromPath,
  syncStockTransferAppShell,
  withStockTransferQuery,
  type StockTransferDraft,
  type StockTransferInboundAccountOption,
  type StockTransferLineItem,
  type StockTransferMarket,
} from './shared';
import './page.css';

export const StockTransferInConfirm = () => {
  const state = reactive({
    market: 'HK' as StockTransferMarket,
    draft: null as StockTransferDraft | null,
    selectedStocks: [] as StockTransferLineItem[],
    inboundAccounts: [] as StockTransferInboundAccountOption[],
    inboundAccountsLoading: false,
    submitting: false,
    /** 当前预览的结单 path */
    previewPath: '',
    /** 结单 path → blob URL（供多图预览） */
    statementBlobByPath: {} as Record<string, string>,
    /** 关闭预览后短暂锁，防止点击穿透立刻再次打开 */
    previewCloseLock: false,
  });

  let previewCloseLockTimer: ReturnType<typeof setTimeout> | null = null;

  const lockPreviewReopen = () => {
    state.previewCloseLock = true;
    if (previewCloseLockTimer) clearTimeout(previewCloseLockTimer);
    previewCloseLockTimer = setTimeout(() => {
      state.previewCloseLock = false;
      previewCloseLockTimer = null;
    }, 320);
  };

  const statementPaths = () => draft()?.statementProofs || [];

  /** 仅图片进入 DiFiPreview；PDF 用系统打开 */
  const statementImagePaths = () =>
    statementPaths().filter((path) => statementProofKindFromPath(path) === 'image');

  const statementPreviewUrls = () =>
    statementImagePaths()
      .map((path) => state.statementBlobByPath[path])
      .filter((url): url is string => !!url);

  const statementPreviewStartIndex = () => {
    const paths = statementImagePaths();
    const active = state.previewPath;
    if (!active) return 0;
    const idx = paths.indexOf(active);
    if (idx < 0) return 0;
    let start = 0;
    for (let i = 0; i < idx; i++) {
      if (state.statementBlobByPath[paths[i]]) start += 1;
    }
    return start;
  };

  const openStatementPreview = (path: string) => {
    if (state.previewCloseLock) return;
    const url = state.statementBlobByPath[path];
    if (!url) {
      showToast(i18n.StockTransferIn.LoadFail);
      return;
    }
    if (statementProofKindFromPath(path) === 'pdf') {
      const opened = window.open(url, '_blank');
      if (!opened) {
        showToast(i18n.StockTransferIn.LoadFail);
      }
      return;
    }
    state.previewPath = path;
  };

  const closeStatementPreview = () => {
    state.previewPath = '';
    lockPreviewReopen();
  };

  const rememberStatementBlob = (path: string, url: string) => {
    if (url) {
      state.statementBlobByPath[path] = url;
      return;
    }
    if (state.statementBlobByPath[path]) {
      const next = { ...state.statementBlobByPath };
      delete next[path];
      state.statementBlobByPath = next;
    }
  };

  const syncInboundSelection = (option: StockTransferInboundAccountOption) => {
    const d = state.draft;
    if (!d) return;
    const next = applyInboundAccountToDraft(d, option);
    state.draft = next;
    setStockTransferDraft(next);
  };

  const loadInboundAccounts = async () => {
    const d = state.draft;
    if (!d) return;
    state.inboundAccountsLoading = true;
    try {
      try {
        await bootDepositApp();
      } catch {
        // 浏览器调试
      }
      try {
        await awaitUserInfoReady();
      } catch {
        // 继续拉账户
      }
      const meta = await fetchStockAccountsMeta();
      state.inboundAccounts = listInboundSecuritiesAccounts(meta, state.market);
      const picked = pickInboundAccountOption(state.inboundAccounts, d);
      if (picked) syncInboundSelection(picked);
    } catch (e) {
      showToastError(e);
    } finally {
      state.inboundAccountsLoading = false;
    }
  };

  const openInboundAccountSheet = () => {
    if (state.inboundAccountsLoading) return;
    if (!state.inboundAccounts.length) {
      showToast({ message: i18n.StockTransferIn.InAccountNumMissing, hud: true });
      return;
    }
    const sheet = showActionSheet({
      title: i18n.FundH5.SelectAccount,
      content: () => {
        const currentNum = resolveInAccountNum(state.draft!);
        return (
        <div>
          {state.inboundAccounts.map((item) => {
            const active = item.accountNum === currentNum;
            return (
              <div
                class={`action-sheet-option${active ? ' action-sheet-option-active' : ''}`}
                onclick={() => {
                  syncInboundSelection(item);
                  sheet.close();
                }}
              >
                <div class="action-sheet-option-label">{item.sheetLabel}</div>
                {active ? <ActionSheetCheckIcon /> : null}
              </div>
            );
          })}
        </div>
        );
      },
    });
  };

  const inboundAccountNumDisplay = () => {
    const d = state.draft;
    if (!d) return '--';
    return displayTransferField(d.inAccountNum || d.settlementAccountNo);
  };

  const inboundAccountTypeDisplay = () => {
    const d = state.draft;
    if (!d) return '--';
    return displayTransferField(displayInAccountTypeForDraft(d));
  };

  const onClose = () => {
    routeReplace(withStockTransferQuery('/h5/stock-transfer-in'));
  };

  const goEditOutBroker = () => {
    const draft = state.draft;
    if (!draft?.outBrokerId) return;
    setStockTransferDraft(draft);
    location.routeTo(
      withStockTransferQuery('/h5/stock-transfer-in/broker', {
        market: state.market,
        brokerId: String(draft.outBrokerId),
      }),
      [0, 0],
    );
  };

  const onNext = async () => {
    if (state.submitting) return;
    const d = state.draft;
    if (!d) return;

    const submitBody = validateStockTransferBeforeSubmit(d, state.selectedStocks);
    if (!submitBody) {
      if (!String(d.outBrokerAccountNo || '').trim()) {
        showToast(i18n.StockTransferIn.PleaseInputAccountNoRequired);
        return;
      }
      if (!String(d.outBrokerAccountName || '').trim()) {
        showToast(i18n.StockTransferIn.PleaseInputOutBrokerAccountName);
        return;
      }
      if (!resolveInAccountNum(d)) {
        showToast({ message: i18n.StockTransferIn.InAccountNumMissing, hud: true });
        return;
      }
      showToast(i18n.StockTransferIn.LoadFail);
      return;
    }

    const saveBody = buildStockTransferApplySaveBody(d);
    if (!saveBody) {
      showToast(i18n.StockTransferIn.LoadFail);
      return;
    }

    state.submitting = true;
    try {
      try {
        await bootDepositApp();
      } catch {
        // 浏览器调试
      }
      try {
        await awaitUserInfoReady();
      } catch {
        // 账户号可能仍来自 draft
      }
      const saveResp = await saveStockTransferInApply(saveBody);
      const applyId =
        parseStockTransferApplyId(saveResp) ||
        submitBody.id ||
        d.applyId ||
        getStockTransferApplyId();
      if (!applyId) {
        showToast(i18n.StockTransferIn.LoadFail);
        return;
      }
      setStockTransferApplyId(applyId);
      setStockTransferDraft({ ...d, applyId });
      location.routeTo(withStockTransferQuery('/h5/stock-transfer-in/signature', { market: state.market }), [0, 0]);
    } catch (e) {
      showToastError(e);
    } finally {
      state.submitting = false;
    }
  };

  onMount(() => {
    syncStockTransferAppShell();
    state.market = readMarketQuery();
    const draft = getStockTransferDraft();
    if (!draft) {
      location.routeTo(withStockTransferQuery('/h5/stock-transfer-in/select-broker', { market: state.market }), [0, 0]);
      return;
    }
    state.draft = draft;
    state.selectedStocks = (draft.stocks || []).filter((item) => item.selected);
    if (!state.selectedStocks.length) {
      location.routeTo(withStockTransferQuery('/h5/stock-transfer-in/stocks', { market: state.market }), [0, 0]);
      return;
    }
    void loadInboundAccounts();
  });

  onCleanup(() => {
    if (previewCloseLockTimer) clearTimeout(previewCloseLockTimer);
  });

  const draft = () => state.draft;

  return (
    <div class="stock-transfer-in-h5">
      <StockTransferNav title={i18n.StockTransferIn.ConfirmPageTitle} onBack={goBackByBrowser} onClose={onClose} />
      <div class="sti-scroll">
        <If when={!!draft()}>
          <section class="sti-confirm-section">
            <StiInfoSectionHead
              title={i18n.StockTransferIn.SectionOutInfo}
              action={
                <button type="button" class="sti-info-section-edit" onclick={goEditOutBroker}>
                  {i18n.StockTransferIn.Edit}
                </button>
              }
            />
            <div class="sti-info-card">
              <StiInfoRow label={i18n.StockTransferIn.LabelBrokerName} value={displayTransferField(draft()?.outBrokerName)} />
              <StiInfoRow
                label={i18n.StockTransferIn.LabelAccountNo}
                value={displayTransferField(draft()?.outBrokerAccountNo)}
              />
              <StiInfoRow
                label={i18n.StockTransferIn.LabelOutAccountName}
                value={displayTransferField(draft()?.outBrokerAccountName)}
              />
              <StiInfoRow
                label={i18n.StockTransferIn.LabelOutContactPerson}
                value={displayTransferField(draft()?.outBrokerContactPerson)}
              />
              <StiInfoRow
                label={i18n.StockTransferIn.LabelOutContactPhone}
                value={displayTransferField(draft()?.outBrokerContactPhone)}
              />
              <If when={(draft()?.statementProofs || []).length > 0}>
                <div class="sti-info-card-link-row">
                  <For each={draft()?.statementProofs || []}>
                    {(path, index) => (
                      <span class="sti-info-card-link" onclick={() => openStatementPreview(path)}>
                        {statementProofFileName(path, index())}
                        <AuthSensitiveImage
                          class="sti-info-card-link-preload"
                          path={path}
                          enablePreview={false}
                          onUrlReady={(url) => rememberStatementBlob(path, url)}
                        />
                      </span>
                    )}
                  </For>
                </div>
              </If>
            </div>
          </section>

          <section class="sti-confirm-section">
            <StiInfoSectionHead title={i18n.StockTransferIn.SectionInInfo} />
            <div class="sti-info-card">
              <StiInfoRow label={i18n.StockTransferIn.LabelBrokerName} value={displayTransferField(draft()?.inBrokerName)} />
              <StiInfoRow
                label={i18n.StockTransferIn.LabelCcassId}
                value={displayTransferField(displayInboundCcassIdForTransfer(state.market, draft()))}
              />
              <div
                class={`sti-confirm-row sti-confirm-row-picker${state.inboundAccountsLoading ? ' sti-confirm-row-picker--disabled' : ''}`}
                onclick={openInboundAccountSheet}
              >
                <div class="sti-confirm-label">{i18n.StockTransferIn.LabelAccountType}</div>
                <div class="sti-confirm-picker-value">
                  <div class="sti-confirm-value">{inboundAccountTypeDisplay()}</div>
                  <svg
                    class="sti-confirm-picker-caret"
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M9.12525 11.8998C9.51442 12.2827 10.1388 12.2827 10.528 11.8998L14.2899 8.19838C14.8001 7.69631 14.4446 6.82813 13.7288 6.82813H5.92444C5.20858 6.82813 4.85308 7.69631 5.36335 8.19838L9.12525 11.8998Z"
                      fill="#A2A7AD"
                    />
                  </svg>
                </div>
              </div>
              <StiInfoRow label={i18n.StockTransferIn.LabelAccountNo} value={inboundAccountNumDisplay()} />
            </div>
          </section>

          <section class="sti-confirm-section">
            <StiInfoSectionHead title={i18n.StockTransferIn.SectionStockInfo} />
            <StiStockInfoCards stocks={state.selectedStocks} />
          </section>
        </If>
      </div>
      <div class="sti-footer">
        <button
          type="button"
          class={`primary-btn btn-rounded width-100${state.submitting ? ' sti-btn-disabled' : ''}`}
          disabled={state.submitting}
          onclick={onNext}
        >
          {i18n.StockTransferIn.NextStep}
        </button>
      </div>
      <DiFiPreview
        visible={
          !!state.previewPath &&
          statementProofKindFromPath(state.previewPath) === 'image' &&
          statementPreviewUrls().length > 0
        }
        urls={statementPreviewUrls()}
        startIndex={statementPreviewStartIndex()}
        onClose={closeStatementPreview}
      />
    </div>
  );
};
